/**
 * MentionPicker - the list of teammates that opens beside a note box when "@" is typed
 * (docs/plans/crm-report-builder-search-collab-plan.md A3.3, A3.4 step 1 and B3.5; WP M-6).
 *
 * Presentational only: MentionInput owns the text, the "@" search, the keys, which row is active and
 * which side of the box the list opens on; this renders the rows (the teammates filterMentionable
 * found, best first) and reports a click or a pointer move.
 *
 *   - It sits beside the box, in the box's own stacking context, at the menu tier of
 *     components/ui/z.ts. It is deliberately NOT portaled: the call workspace is a fixed z-40 layer
 *     (Z.overlay), so a picker portaled to <body> at the menu tier would open underneath it; in place
 *     it inherits the page's font and theme, so there is nothing to re-add.
 *   - A press anywhere on it is prevented, so the box keeps the keyboard, the caret and the list
 *     while a row is clicked (the pick itself happens on click).
 *   - Rows are 44 px tall below sm: and compact from sm: up; the active row carries the brand tint.
 *   - At MENTIONS_PER_NOTE_MAX named teammates it shows one line saying so instead of the list.
 */
import { useEffect, useRef } from 'react';
import { Z } from '../components/ui/z';
import { MENTIONS_PER_NOTE_MAX } from './mentionLogic';
import type { MentionableUser } from './types';

/** Which side of the box the list opens on. */
export type MentionPlacement = 'above' | 'below';

export interface MentionPickerProps {
  /** The listbox id: the box points at it with aria-controls. */
  id: string;
  /** The teammates to offer, best first (filterMentionable), never empty while shown. */
  users: ReadonlyArray<MentionableUser>;
  /** The highlighted row: arrows move it, Enter or Tab picks it. */
  activeIndex: number;
  /** The DOM id of row i: the box's aria-activedescendant. */
  optionId: (index: number) => string;
  /** A row was clicked. */
  onPick: (user: MentionableUser) => void;
  /** The pointer moved onto row i. */
  onActiveChange: (index: number) => void;
  /** 'above' the box (the default) or 'below' it. */
  placement?: MentionPlacement;
  /** A tighter height for the list, in px, when the side it opens on has little room; null = its own. */
  maxHeight?: number | null;
  /** The note already names MENTIONS_PER_NOTE_MAX teammates: say so instead of listing anyone. */
  full?: boolean;
}

const PANEL =
  'absolute left-0 w-64 max-w-full rounded-lg border border-gray-200 bg-white py-1 text-left shadow-lg dark:border-gray-700 dark:bg-gray-800';

const ROW =
  'flex min-h-[44px] cursor-pointer select-none items-center rounded-md px-2.5 py-2 text-sm text-gray-800 transition-colors duration-150 dark:text-gray-100 sm:min-h-0 sm:py-1.5';

const ROW_ACTIVE = 'bg-primary-50 dark:bg-primary-900/20';

export default function MentionPicker({
  id,
  users,
  activeIndex,
  optionId,
  onPick,
  onActiveChange,
  placement = 'above',
  maxHeight = null,
  full = false,
}: MentionPickerProps) {
  const listRef = useRef<HTMLDivElement | null>(null);

  // Keep the active row in view when the arrows walk past the list's scrolled edge.
  useEffect(() => {
    const row = listRef.current?.querySelector<HTMLElement>(`[data-mention-index="${activeIndex}"]`);
    row?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex]);

  const side = placement === 'below' ? 'top-full mt-1' : 'bottom-full mb-1';

  return (
    <div
      data-mention-picker=""
      data-placement={placement}
      className={`${PANEL} ${side} ${Z.menu}`}
      onMouseDown={(e) => {
        // The box keeps the focus and the caret: the pick happens on click.
        e.preventDefault();
      }}
    >
      {full ? (
        <p role="status" className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-300">
          A note can mention at most {MENTIONS_PER_NOTE_MAX} teammates.
        </p>
      ) : (
        <div
          ref={listRef}
          id={id}
          role="listbox"
          aria-label="Teammates to mention"
          className="max-h-48 overflow-y-auto overscroll-contain px-1 sm:max-h-56"
          style={maxHeight ? { maxHeight } : undefined}
        >
          {users.map((user, index) => {
            const active = index === activeIndex;
            return (
              <div
                key={user.id}
                id={optionId(index)}
                role="option"
                aria-selected={active}
                data-mention-index={index}
                data-mention-option={user.id}
                onClick={() => onPick(user)}
                onMouseMove={() => {
                  if (!active) onActiveChange(index);
                }}
                className={`${ROW} ${active ? ROW_ACTIVE : ''}`}
              >
                <span className="min-w-0 truncate">{user.name}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
